import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  ITcComponentCertifiedWeightInformation,
  ITcComponentRawMaterialInformation,
} from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { useParams } from "react-router-dom";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";
import {
  deriveComponentCertifiedWeight,
  makeIsCertifiedRM,
} from "./component-payload";

interface ComponentCertifiedWeightProps {
  formRef: React.MutableRefObject<any>;
  setCertifiedWeightInfoData: (data: ITcComponentCertifiedWeightInformation[]) => void;
  rawMaterialInfoData: ITcComponentRawMaterialInformation[];
  componentNetWeight: number;
}

/**
 * C-Step 3 — Component Certified Weight Information.
 * READ-ONLY group-by: certified component raw materials grouped by their claims category, with
 * allocated weight (= component net * RM% / 100) summed per category. Mirrors the product
 * certified-weight step. The derived rows are pushed up so the component payload uses them.
 */
const ComponentCertifiedWeight: React.FC<ComponentCertifiedWeightProps> = ({
  formRef,
  setCertifiedWeightInfoData,
  rawMaterialInfoData,
  componentNetWeight,
}) => {
  const { notify } = useNotification();
  const { TE_ID } = useParams();
  const { executeGet } = useApi();

  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [tcStandardList, setTcStandardList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const lastPushed = useRef<string>("");

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const [certResp, baseResp] = await Promise.all([
          executeGet(TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES),
          executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID }),
        ]);
        const certList = certResp?.data?.response || certResp?.data;
        setCertifiedRawMaterialCodes(Array.isArray(certList) ? certList : []);
        const standards = baseResp?.data?.tcStandard;
        setTcStandardList(Array.isArray(standards) ? standards : []);
      } catch (err) {
        Utills.cl(`Error initializing component certified weight step: ${err}`);
        notify("Failed to load certified weight information", "error");
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  const isCertifiedRM = makeIsCertifiedRM(certifiedRawMaterialCodes, tcStandardList);

  const grouped = useMemo<ITcComponentCertifiedWeightInformation[]>(() => {
    const ready =
      Array.isArray(certifiedRawMaterialCodes) &&
      certifiedRawMaterialCodes.length > 0 &&
      Array.isArray(tcStandardList) &&
      tcStandardList.length > 0;
    return deriveComponentCertifiedWeight(
      componentNetWeight,
      Array.isArray(rawMaterialInfoData) ? rawMaterialInfoData : [],
      isCertifiedRM,
      ready
    );
  }, [rawMaterialInfoData, componentNetWeight, certifiedRawMaterialCodes, tcStandardList]);

  const totalCertifiedWeight = useMemo(
    () =>
      grouped.reduce(
        (sum, r) => sum + Number(r.tcProductComponentCertifiedWeight || 0),
        0
      ),
    [grouped]
  );

  // Push derived grouped data up so the component POST uses it (loop-guarded).
  useEffect(() => {
    const serialized = JSON.stringify(grouped);
    if (serialized !== lastPushed.current) {
      lastPushed.current = serialized;
      setCertifiedWeightInfoData(grouped);
    }
  }, [grouped]);

  // Read-only step: never block Next; expose matching sums for the parent's gate.
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        editingRowIndex: -1,
        hasUnsavedRow: false,
        tableSum: totalCertifiedWeight,
        expectedTotal: totalCertifiedWeight,
        validateForm: async () => ({}),
      };
    }
  }, [formRef, totalCertifiedWeight]);

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <>
          <Box mb={2}>
            <Typography>
              <span style={{ fontWeight: 600 }}>Total Component Certified Weight: </span>
              {totalCertifiedWeight.toFixed(2)} kg
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Auto-derived from the certified raw materials in the Component Raw Material step,
              grouped by claims category. This table is read only.
            </Typography>
          </Box>
          {grouped.length === 0 ? (
            <Typography color="text.secondary" sx={{ my: 2 }}>
              No certified raw materials with a claims category yet. Add certified raw materials and
              select their claims category in the Component Raw Material step.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#2C4748" }}>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      Component Claims Category
                    </TableCell>
                    <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                      Component Claims Category Certified Weight
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped.map((row, i) => (
                    <TableRow key={`${row.tcProductComponentClaimsCategory}-${i}`}>
                      <TableCell>{row.tcProductComponentClaimsCategory}</TableCell>
                      <TableCell>
                        {Number(row.tcProductComponentCertifiedWeight || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default ComponentCertifiedWeight;
